<template>
  <section v-if="additional?.length" class="additional pe-section">
    <h3>
      Additional
      <SectionActions
        v-show="isPowerEdit"
        class="actions"
        section="additional"
        :actions="['delete', 'add']"
      />
    </h3>
    <section class="list">
      <ul class="list-disc list-outside">
        <template v-for="(item, index) in additional" :key="item.description">
          <li
            v-if="item.description"
            :data-index="index"
            :data-name="item?.description"
            @keydown.enter.stop.prevent="e => addItem(e, index)"
            @keydown.delete.stop="e => removeAdditional(e, index)"
            class="additionalItem"
          >
            <span
              v-editable="isPowerEdit"
              :data-target-path="`resume.additional.${index}.description`"
              v-text="item.description"
            ></span>
          </li>
        </template>
      </ul>
    </section>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType, toRefs, nextTick } from 'vue'
import { Resume } from '@/types/resume/v1'
import SectionActions from './SectionActions.vue'
import ItemActions from './ItemActions.vue'
import { useStructuredResumeStore } from '@/store/pinia/structuredResume'

export default defineComponent({
  name: 'Additional',
  components: {
    SectionActions,
    ItemActions,
  },
  props: {
    additional: {
      type: Array as PropType<Resume.Additional[]>,
      required: false,
      default: () => [],
    },
  },
  setup(props) {
    const store = useStructuredResumeStore()
    const { additional } = toRefs(props)
    const addItem = async (event, index) => {
      additional.value.splice(index + 1, 0, {
        description: 'New item...',
      })
      await nextTick()
      const nextItem = event.currentTarget?.parentElement
        ?.querySelector(`li.additionalItem[data-index="${index + 1}"]`)
        .querySelector('span')
      console.log(nextItem)
      await nextTick()
      focusElement(nextItem)
    }

    const removeAdditional = async (event, index) => {
      if (event.currentTarget?.innerText.trim().length > 0) {
        return
      }
      event.preventDefault()
      const previousItem = event.currentTarget?.parentElement
        ?.querySelector(`li.additionalItem[data-index="${index - 1}"]`)
        .querySelector('span')
      setCursor(previousItem)
      store.cleanAdditional()
    }

    const setCursor = element => {
      if (!element) return
      element.focus()
      const range = document.createRange()
      const selection = window.getSelection()
      range.setStart(element, element.childNodes.length)
      range.setEnd(element, element.childNodes.length)
      range.collapse(true)
      selection?.removeAllRanges()
      selection?.addRange(range)
    }

    const focusElement = element => {
      element.focus()
      const selection = window.getSelection()
      selection?.removeAllRanges()
      const range = document.createRange()
      range.selectNodeContents(element)
      selection?.addRange(range)
    }

    return {
      isPowerEdit: store.isPowerEdit,
      addItem,
      removeAdditional,
    }
  },
})
</script>

<style lang="postcss" scoped>
.additional {
  @apply text-base-em leading-[inherit] pt-6-em @container;
  h3 {
    @apply leading-none text-xl-em flex items-center;
    .actions {
      @apply hidden;
    }
    &:hover {
      .actions {
        @apply flex;
      }
    }
  }
  .list {
    @apply pt-4-em;
    .additionalItem {
      @apply text-sm-em relative min-w-[6px] h-auto ml-4-em leading-[inherit];
      .itemActions {
        @apply hidden;
      }
      &:hover {
        .itemActions {
          @apply flex;
        }
      }
    }
  }
}
</style>
